import { resolveBinding, rowsOf } from "@southfields-digital/mxmz-binding";

/** jsonata binds a key verbatim, so `index` is what a path writes as `$index`. */
type ResolutionContext = {
	index?: number;
};

type ResolveDatafieldParams = {
	property: Properties;
	binding: DataBinding;
	data: unknown;
	context?: ResolutionContext;
};

type ResolveRepeaterParams = {
	repeater: RepeaterProperties;
	repeaterBinding: DataBinding;
	data: unknown;
	rootData: unknown;
	context?: ResolutionContext;
};

type ResolveDatafieldsParams = {
	properties: Properties[];
	data: unknown;
};

type ResolvedPropertyData = {
	propertyValues: PropertyValue;
	errors: Record<string, string>;
};

export async function resolveDatafield(
	params: ResolveDatafieldParams,
): Promise<{ value: string; error?: string }> {
	const { property, binding, data, context } = params;

	const result = await resolveBinding(binding.path, data, { root: data, ...context });

	if (result.error) {
		const fallbackValue = property.defaultValue ?? "";
		return {
			value: fallbackValue,
			error: result.error,
		};
	}

	return {
		value: String(result.value ?? ""),
		error: undefined,
	};
}

async function resolveChildProperty(params: {
	child: Properties;
	childBinding: DataBinding | undefined;
	itemData: unknown;
	rootData: unknown;
	context: ResolutionContext;
}): Promise<{ value: string | PropertyValue[]; error?: string }> {
	const { child, childBinding, itemData, rootData, context } = params;

	if (!childBinding) {
		return {
			value: child.defaultValue ?? "",
			error: undefined,
		};
	}

	if (child.type === "repeater") {
		const nestedResult = await resolveRepeater({
			repeater: child,
			repeaterBinding: childBinding,
			data: itemData,
			rootData,
			context,
		});

		return {
			value: nestedResult.error ? [] : nestedResult.items,
			error: nestedResult.error,
		};
	}

	const result = await resolveBinding(childBinding.path, itemData, {
		root: rootData,
		...context,
	});

	if (result.error) {
		return {
			value: child.defaultValue ?? "",
			error: result.error,
		};
	}

	return {
		value: String(result.value ?? ""),
		error: undefined,
	};
}

async function resolveRepeaterItem(params: {
	repeater: RepeaterProperties;
	itemData: unknown;
	rootData: unknown;
	context: ResolutionContext;
}): Promise<PropertyValue> {
	const { repeater, itemData, rootData, context } = params;
	const itemValues: PropertyValue = {};

	const resolutionPromises = repeater.children.map(async (child) => {
		if (!child.id) {
			return null;
		}

		const childBinding = child.dataBinding;

		if (!childBinding) {
			return {
				childId: child.id,
				value: child.defaultValue,
			};
		}

		const childResult = await resolveChildProperty({
			child,
			childBinding,
			itemData,
			rootData,
			context,
		});

		return {
			childId: child.id,
			value: childResult.value,
		};
	});

	const results = await Promise.all(resolutionPromises);

	for (const result of results) {
		if (result && result.value !== undefined) {
			itemValues[result.childId] = result.value;
		}
	}

	return itemValues;
}

export async function resolveRepeater(params: ResolveRepeaterParams): Promise<{
	items: PropertyValue[];
	error?: string;
}> {
	const { repeater, repeaterBinding, data, rootData, context: outer } = params;

	const repeaterResult = await resolveBinding(repeaterBinding.path, data, {
		root: rootData,
		...outer,
	});

	if (repeaterResult.error) {
		return {
			items: [],
			error: repeaterResult.error,
		};
	}

	const { rows, error: rowsError } = rowsOf(repeaterResult.value);

	if (rowsError) {
		return {
			items: [],
			error: rowsError,
		};
	}

	const itemPromises = rows.map((itemData, index) => {
		const context: ResolutionContext = { index };

		return resolveRepeaterItem({
			repeater,
			itemData,
			rootData,
			context,
		});
	});

	const items = await Promise.all(itemPromises);

	return {
		items,
		error: undefined,
	};
}

export const resolveDatafields = async (
	params: ResolveDatafieldsParams,
): Promise<ResolvedPropertyData> => {
	const { properties, data } = params;

	const propertyPromises = properties.map(async (property) => {
		if (!property.id) {
			return null;
		}

		if (property.type === "repeater") {
			const repeaterBinding = property.dataBinding;

			if (!repeaterBinding) {
				return null;
			}

			const repeaterResult = await resolveRepeater({
				repeater: property,
				repeaterBinding,
				data,
				rootData: data,
			});

			return {
				propertyId: property.id,
				value: repeaterResult.items,
				error: repeaterResult.error,
			};
		} else {
			const binding = property.dataBinding;

			if (!binding) {
				return null;
			}

			const scalarResult = await resolveDatafield({
				property,
				binding,
				data,
			});

			return {
				propertyId: property.id,
				value: scalarResult.value,
				error: scalarResult.error,
			};
		}
	});

	const results = await Promise.all(propertyPromises);

	const propertyValues: PropertyValue = {};
	const errors: Record<string, string> = {};

	for (const result of results) {
		if (!result) {
			continue;
		}

		propertyValues[result.propertyId] = result.value;

		if (result.error) {
			errors[result.propertyId] = result.error;
		}
	}

	return {
		propertyValues,
		errors,
	};
};
